import { lazy, Suspense } from 'react';
import type { TableDefinition } from '../engine/tables/definition';

const loadDebugCanvas = () => import('./DebugCanvasImpl');
const DebugCanvasImpl = lazy(loadDebugCanvas);

export async function warm2DRendererChunkSafely(load: () => Promise<unknown>): Promise<void> {
  try {
    await load();
  } catch {
    // Idle chunk warmup is opportunistic. A transient network/deploy failure
    // must not surface as an unhandled rejection or prevent a later PLAY retry.
  }
}

export function shouldDefer2DRendererWarmup(visibilityState: DocumentVisibilityState): boolean {
  return visibilityState === 'hidden';
}

export function canSchedule2DRendererWarmup(hasWindow: boolean, hasDocument: boolean): boolean {
  return hasWindow && hasDocument;
}

interface VisibilityDocument {
  visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export function run2DRendererWarmupWhenVisible(
  documentLike: VisibilityDocument,
  warm: () => void,
): void {
  if (!shouldDefer2DRendererWarmup(documentLike.visibilityState)) {
    warm();
    return;
  }

  const onVisibilityChange = () => {
    if (shouldDefer2DRendererWarmup(documentLike.visibilityState)) return;
    documentLike.removeEventListener('visibilitychange', onVisibilityChange);
    warm();
  };
  documentLike.addEventListener('visibilitychange', onVisibilityChange);
}

function schedule2DRendererWarmup(): void {
  const warm = () => {
    run2DRendererWarmupWhenVisible(document, () => {
      void warm2DRendererChunkSafely(loadDebugCanvas);
    });
  };
  if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 1200 });
  else globalThis.setTimeout(warm, 700);
}

// Keep the alternate 2D painter out of the initial 3D arcade graph. Warm it
// later during idle so returning 2D players normally have the chunk before PLAY
// without making first paint parse the complete canvas renderer. Re-check
// visibility when the scheduled work actually fires as well as when it is first
// scheduled, so a tab backgrounded in between does not spend bandwidth/CPU on
// a renderer the player cannot currently use. Some prerender/WebView-like
// environments expose window without a usable document; skip only this optional
// warmup there instead of throwing during module initialization.
if (canSchedule2DRendererWarmup(typeof window !== 'undefined', typeof document !== 'undefined')) {
  run2DRendererWarmupWhenVisible(document, schedule2DRendererWarmup);
}

export default function DebugCanvas({ table }: { table: TableDefinition }) {
  return (
    <Suspense fallback={<canvas className="playfield2d" style={{position:'absolute',inset:0,width:'100%',height:'100%',display:'block'}} aria-label="Pinball table loading" />}>
      <DebugCanvasImpl table={table} />
    </Suspense>
  );
}
